import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import api from "@/lib/api";
import { TERMS_VERSION } from "@/data/legalContent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import GoogleButton from "@/components/xuma/GoogleButton";
import Logo from "@/components/xuma/Logo";

const planLabels = { basic: "Plan Básico", premium: "Plan Premium", founder: "Oferta Fundadores" };

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const plan = params.get("plan");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error("Debes aceptar los Términos y el Aviso de Privacidad.");
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password);
      api.post("/legal/accept", { version: TERMS_VERSION }).catch(() => {});
      toast.success("¡Cuenta creada! Bienvenido a Xuma.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-xuma-bg grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-xuma-dark text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-[0.07]" />
        <Link to="/" className="flex items-center relative">
          <Logo variant="light" size="md" />
        </Link>
        <div className="relative">
          <h2 className="font-heading font-extrabold text-4xl leading-tight">Cada contacto puede convertirse en una oportunidad</h2>
          <p className="mt-4 text-white/60 max-w-sm">Crea tu tarjeta digital interactiva y empieza a sumar clientes hoy mismo.</p>
        </div>
        <p className="font-heading text-lg text-white/80 relative">Xuma — Cada contacto suma.</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-xuma-muted hover:text-xuma-dark mb-8">
            <ArrowLeft className="h-4 w-4" /> Volver
          </Link>
          <h1 className="font-heading font-extrabold text-3xl text-xuma-dark">Crear cuenta</h1>
          <p className="mt-2 text-xuma-muted">
            {plan && planLabels[plan] ? `Empieza con el ${planLabels[plan]}.` : "Empieza a crear tu tarjeta digital."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="name">Nombre completo</Label>
              <Input id="name" data-testid="register-name" required value={name}
                onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="email">Correo</Label>
              <Input id="email" data-testid="register-email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.com" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <Input id="password" data-testid="register-password" type="password" required minLength={6} value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres" className="mt-1.5 h-11" />
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer select-none">
              <input type="checkbox" data-testid="register-terms" checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-xuma-border accent-[#EF5A2A]" />
              <span className="text-xs leading-relaxed text-xuma-muted">
                He leído y acepto los{" "}
                <Link to="/legal/terminos-y-condiciones" target="_blank" className="text-xuma-primary hover:underline">Términos y Condiciones</Link>{" "}
                y el{" "}
                <Link to="/legal/aviso-de-privacidad" target="_blank" className="text-xuma-primary hover:underline">Aviso de Privacidad</Link>.
              </span>
            </label>
            <Button type="submit" data-testid="register-submit" disabled={loading || !accepted}
              className="w-full h-11 rounded-full bg-xuma-primary hover:bg-xuma-primaryHover text-white disabled:opacity-60">
              {loading ? "Creando..." : "Crear mi cuenta"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-xuma-muted">
            <span className="h-px flex-1 bg-xuma-border" /> o <span className="h-px flex-1 bg-xuma-border" />
          </div>
          <GoogleButton label="Continuar con Google" />
          <p className="mt-3 text-[11px] leading-relaxed text-center text-xuma-muted">
            Al continuar con Google, aceptas los{" "}
            <Link to="/legal/terminos-y-condiciones" target="_blank" className="text-xuma-primary hover:underline">Términos</Link>{" "}
            y el{" "}
            <Link to="/legal/aviso-de-privacidad" target="_blank" className="text-xuma-primary hover:underline">Aviso de Privacidad</Link>.
          </p>

          <p className="mt-8 text-sm text-center text-xuma-muted">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="text-xuma-primary font-medium hover:underline">Entrar</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
